// PRD.316 — tooth/surface picker for a Clinical Finding.
//
// Reuses the EXISTING `OdontogramChart` (PRD §9.2 — "do not build a second
// incompatible odontogram") in multi-select mode, so tooth numbering, layout
// and chart-state rendering are the same component the Part Chart uses. It is
// a SELECTOR only: nothing here writes to `odontogram_charts`.
//
// Surfaces use the canonical vocabulary from data-access, never a competing
// coding convention (PRD §7.3).

import { useCallback, useState } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import {
  TOOTH_SURFACES,
  TOOTH_SURFACE_INITIALS,
  TOOTH_SURFACE_LABELS,
  type FindingTooth,
  type ToothStatesMap,
} from '@helios/data-access';
import { OdontogramChart } from './OdontogramChart';

export function ToothPickerModal({
  open,
  initialTeeth,
  toothStates,
  primary,
  showSurfaces = true,
  suggestedTeeth,
  onSave,
  onClose,
}: {
  open: boolean;
  initialTeeth: FindingTooth[];
  /** Live chart state, purely so the picker shows the same visual context. */
  toothStates: ToothStatesMap;
  primary: string;
  /**
   * PRD.317 — Diagnosis associates teeth only, no surfaces (surface context
   * belongs to the chart and to Clinical Findings). Defaults on, so PRD.316
   * keeps its surface rows unchanged.
   */
  showSurfaces?: boolean;
  /**
   * PRD.317 — teeth already documented on this encounter's Clinical Findings,
   * offered as one-tap context. TOOTH NUMBERS ONLY: tapping one selects a
   * tooth, it never carries a narrative or condition across, and it never
   * implies a diagnosis. Empty/omitted when Clinical Findings is absent.
   */
  suggestedTeeth?: readonly string[];
  onSave: (teeth: FindingTooth[]) => void;
  onClose: () => void;
}) {
  // Seeded once on mount, which is also once per opening: the caller renders
  // this modal only while a finding is being edited, so each open gets a fresh
  // component and no previous finding's abandoned edits can leak in.
  const [teeth, setTeeth] = useState<FindingTooth[]>(initialTeeth);

  const toggleTooth = useCallback((num: number) => {
    const key = String(num);
    setTeeth((prev) =>
      prev.some((t) => t.tooth === key)
        ? prev.filter((t) => t.tooth !== key)
        : // Keep ascending FDI order so the chip reads naturally (31, 32, 41).
          [...prev, { tooth: key, surfaces: [] }].sort((a, b) => Number(a.tooth) - Number(b.tooth)),
    );
  }, []);

  const toggleSurface = useCallback((tooth: string, surface: string) => {
    setTeeth((prev) =>
      prev.map((t) =>
        t.tooth === tooth
          ? {
              ...t,
              surfaces: t.surfaces.includes(surface)
                ? t.surfaces.filter((s) => s !== surface)
                : [...t.surfaces, surface],
            }
          : t,
      ),
    );
  }, []);

  const selected = new Set(teeth.map((t) => Number(t.tooth)));

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Select teeth"
      description="Optional. Leave empty for a general finding."
      maxWidth={980}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
          <Button variant="outline" size="lg" onClick={() => setTeeth([])}>
            Clear
          </Button>
          <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
            <Button variant="outline" size="lg" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="gradient" size="lg" onClick={() => onSave(teeth)}>
              Save
            </Button>
          </div>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5)',
        }}
      >
        {suggestedTeeth && suggestedTeeth.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              Suggested tooth context
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
              {suggestedTeeth.map((tooth) => (
                <button
                  key={tooth}
                  onClick={() => toggleTooth(Number(tooth))}
                  title="Use this tooth"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    borderRadius: 'var(--radius-full)',
                    border: `1px dashed ${primary}`,
                    background: selected.has(Number(tooth)) ? primary : 'transparent',
                    color: selected.has(Number(tooth)) ? '#fff' : primary,
                    fontSize: 'var(--font-size-xs)',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>+</span>
                  {tooth}
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <OdontogramChart
            toothStates={toothStates}
            selectedTooth={null}
            selectedTeeth={selected}
            onToothClick={toggleTooth}
            primary={primary}
          />
        </div>

        {/* Per-tooth surfaces. Only shown for teeth actually chosen, so a
            multi-tooth finding is not buried under five surface rows each. */}
        {showSurfaces && teeth.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            {teeth.map((t) => (
              <div
                key={t.tooth}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-3)',
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    minWidth: 34,
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text)',
                  }}
                >
                  {t.tooth}
                </span>
                {TOOTH_SURFACES.map((surf) => {
                  const on = t.surfaces.includes(surf);
                  return (
                    <button
                      key={surf}
                      onClick={() => toggleSurface(t.tooth, surf)}
                      title={TOOTH_SURFACE_LABELS[surf]}
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 'var(--radius-sm)',
                        border: `1px solid ${on ? primary : 'var(--border)'}`,
                        background: on ? primary : 'transparent',
                        color: on ? '#fff' : 'var(--text-muted)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      {TOOTH_SURFACE_INITIALS[surf]}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
